/**
 * The Arrhenius equation, k = A·e^(−Ea/RT), in its four useful shapes:
 *
 *  - solve k, A, Ea or T from the other three;
 *  - Ea (or k₂, or T₂) from rate constants at two temperatures,
 *    ln(k₂/k₁) = (Ea/R)(1/T₁ − 1/T₂);
 *  - the factor by which the rate changes for a given rise in temperature;
 *  - an Arrhenius plot: least-squares line of ln k against 1/T, slope = −Ea/R.
 *
 * Works in joules per mole and kelvin with R = 8.314 462 618 J/(mol·K) (exact). A and k
 * share whatever unit the rate constant has, so no unit conversion applies to them.
 */

import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import { GAS_CONSTANT } from "./constants";
import { MOLAR_ENERGY_UNITS } from "./gibbs";
import { type HeatFailure, type ReadSpec, readAll, unitFactor } from "./heat";
import { degreeSize, fromKelvin, toKelvin } from "./units";

export type ArrheniusFailure =
  | HeatFailure
  | {
      ok: false;
      reason:
        | "negativeEa"
        | "rateAboveA"
        | "sameTemperature"
        | "outOfRange"
        | "noTemperature"
        | "tooFewPoints"
        | "badLine"
        | "allSameTemperature"
        | "zeroEa";
      /** Line number for a bad data line. */
      line?: number;
    };

const R = GAS_CONSTANT;

function eaFactor(unit: string): number {
  return unitFactor(MOLAR_ENERGY_UNITS, unit);
}

/* ------------------------------------------------ k = A e^(−Ea/RT) */

export type ArrheniusTerm = "k" | "a" | "ea" | "t";
export const ARRHENIUS_TERMS: readonly ArrheniusTerm[] = ["k", "a", "ea", "t"];

export interface ArrheniusInput {
  unknown: ArrheniusTerm;
  text: Record<ArrheniusTerm, string>;
  unit: { ea: string; t: string };
}

export interface ArrheniusSolution {
  ok: true;
  unknown: ArrheniusTerm;
  /** k, A in the rate constant's unit; Ea in J/mol; T in K. */
  k: number;
  a: number;
  ea: number;
  kelvin: number;
  /** Ea ÷ RT, the exponent. */
  exponent: number;
  answer: number;
  answerUnit: string;
}

export type ArrheniusOutcome = ArrheniusSolution | ArrheniusFailure;

const KINDS = {
  k: "positive",
  a: "positive",
  ea: "any",
  t: "temperature",
} as const;

function arrheniusValue(
  unknown: ArrheniusTerm,
  v: Record<string, number>
): { value: number } | ArrheniusFailure {
  if (unknown === "k") {
    return { value: v.a * Math.exp(-v.ea / (R * v.t)) };
  }
  if (unknown === "a") {
    return { value: v.k * Math.exp(v.ea / (R * v.t)) };
  }
  if (v.k > v.a) {
    return { ok: false, reason: "rateAboveA" };
  }
  const lnRatio = Math.log(v.k / v.a);
  if (unknown === "ea") {
    return { value: -R * v.t * lnRatio };
  }
  if (lnRatio === 0) {
    return { ok: false, reason: "noTemperature" };
  }
  const t = -v.ea / (R * lnRatio);
  return t > 0 ? { value: t } : { ok: false, reason: "noTemperature" };
}

export function solveArrhenius(input: ArrheniusInput): ArrheniusOutcome {
  const { unknown, unit } = input;
  const read = readAll(
    ARRHENIUS_TERMS.filter((term) => term !== unknown).map(
      (term): ReadSpec => ({
        key: term,
        text: input.text[term],
        kind: KINDS[term],
        unit: term === "t" ? unit.t : "",
        factor: term === "ea" ? eaFactor(unit.ea) : 1,
      })
    )
  );
  if ("ok" in read) {
    return read;
  }
  if (unknown !== "ea" && read.values.ea < 0) {
    return { ok: false, reason: "negativeEa" };
  }
  const solved = arrheniusValue(unknown, read.values);
  if ("ok" in solved) {
    return solved;
  }
  const full = { ...read.values, [unknown]: solved.value } as Record<
    ArrheniusTerm,
    number
  >;
  if (
    !(
      Number.isFinite(full.k) &&
      Number.isFinite(full.a) &&
      full.k > 0 &&
      full.a > 0
    )
  ) {
    return { ok: false, reason: "outOfRange" };
  }
  if (unknown === "ea" && full.ea < 0) {
    return { ok: false, reason: "negativeEa" };
  }
  const answers = {
    k: full.k,
    a: full.a,
    ea: full.ea / eaFactor(unit.ea),
    t: fromKelvin(full.t, unit.t),
  };
  return {
    ok: true,
    unknown,
    k: full.k,
    a: full.a,
    ea: full.ea,
    kelvin: full.t,
    exponent: full.ea / (R * full.t),
    answer: answers[unknown],
    answerUnit: unknown === "ea" ? unit.ea : unit.t,
  };
}

/* ------------------------------------------------------ two points */

export type TwoPointTerm = "ea" | "k2" | "t2";

export interface TwoPointInput {
  unknown: TwoPointTerm;
  text: { k1: string; t1: string; k2: string; t2: string; ea: string };
  unit: { ea: string; t: string };
}

export interface TwoPointSolution {
  ok: true;
  unknown: TwoPointTerm;
  /** k in the rate constant's unit; T in K; Ea in J/mol. */
  k1: number;
  k2: number;
  t1: number;
  t2: number;
  ea: number;
  /** A from the first point: k₁·e^(Ea/RT₁). */
  a: number;
  answer: number;
  answerUnit: string;
}

export type TwoPointOutcome = TwoPointSolution | ArrheniusFailure;

const TWO_KINDS = {
  k1: "positive",
  k2: "positive",
  t1: "temperature",
  t2: "temperature",
  ea: "any",
} as const;

export function solveTwoPoint(input: TwoPointInput): TwoPointOutcome {
  const { unknown, unit } = input;
  const keys = ["k1", "t1", "k2", "t2", "ea"] as const;
  const wanted = keys.filter(
    (key) => key !== (unknown === "ea" ? "ea" : unknown)
  );
  const read = readAll(
    wanted.map(
      (key): ReadSpec => ({
        key,
        text: input.text[key],
        kind: TWO_KINDS[key],
        unit: key.startsWith("t") ? unit.t : "",
        factor: key === "ea" ? eaFactor(unit.ea) : 1,
      })
    )
  );
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  if (unknown !== "t2" && v.t1 === v.t2) {
    return { ok: false, reason: "sameTemperature" };
  }
  let { k2, t2, ea } = v;
  if (unknown === "ea") {
    ea = (R * Math.log(v.k2 / v.k1)) / (1 / v.t1 - 1 / v.t2);
  } else if (unknown === "k2") {
    k2 = v.k1 * Math.exp((v.ea / R) * (1 / v.t1 - 1 / v.t2));
  } else {
    if (v.ea === 0) {
      return { ok: false, reason: "zeroEa" };
    }
    const inverse = 1 / v.t1 - (R * Math.log(v.k2 / v.k1)) / v.ea;
    if (!(inverse > 0)) {
      return { ok: false, reason: "noTemperature" };
    }
    t2 = 1 / inverse;
  }
  if (!(Number.isFinite(k2) && Number.isFinite(ea) && k2 > 0)) {
    return { ok: false, reason: "outOfRange" };
  }
  const answers = {
    ea: ea / eaFactor(unit.ea),
    k2,
    t2: fromKelvin(t2, unit.t),
  };
  return {
    ok: true,
    unknown,
    k1: v.k1,
    k2,
    t1: v.t1,
    t2,
    ea,
    a: v.k1 * Math.exp(ea / (R * v.t1)),
    answer: answers[unknown],
    answerUnit: unknown === "ea" ? unit.ea : unit.t,
  };
}

/* ------------------------------------------------ rate change with T */

export type RiseTerm = "factor" | "ea";

export interface RiseInput {
  unknown: RiseTerm;
  text: { ea: string; t: string; dt: string; factor: string };
  unit: { ea: string; t: string };
}

export interface RiseSolution {
  ok: true;
  unknown: RiseTerm;
  /** J/mol, K, K (rise), and k(T+ΔT) ÷ k(T). */
  ea: number;
  t: number;
  dt: number;
  factor: number;
  answer: number;
  answerUnit: string;
}

export type RiseOutcome = RiseSolution | ArrheniusFailure;

export function riseFactor(ea: number, t: number, dt: number): number {
  return Math.exp((ea / R) * (1 / t - 1 / (t + dt)));
}

export function solveRise(input: RiseInput): RiseOutcome {
  const { unknown, unit } = input;
  const dtText = input.text.dt;
  const read = readAll(
    (["ea", "t", "dt", "factor"] as const)
      .filter((key) => key !== unknown)
      .map((key): ReadSpec => {
        if (key === "ea") {
          return {
            key,
            text: input.text.ea,
            kind: "positive",
            unit: "",
            factor: eaFactor(unit.ea),
          };
        }
        if (key === "t") {
          return {
            key,
            text: input.text.t,
            kind: "temperature",
            unit: unit.t,
            factor: 1,
          };
        }
        if (key === "dt") {
          return {
            key,
            text: dtText,
            kind: "any",
            unit: "",
            factor: degreeSize(unit.t),
          };
        }
        return {
          key,
          text: input.text.factor,
          kind: "positive",
          unit: "",
          factor: 1,
        };
      })
  );
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  if (v.dt === 0) {
    return { ok: false, reason: "sameTemperature" };
  }
  if (!(v.t + v.dt > 0)) {
    return { ok: false, reason: "noTemperature" };
  }
  const ea =
    unknown === "ea"
      ? (R * Math.log(v.factor)) / (1 / v.t - 1 / (v.t + v.dt))
      : v.ea;
  const factor = unknown === "factor" ? riseFactor(v.ea, v.t, v.dt) : v.factor;
  if (!(Number.isFinite(factor) && Number.isFinite(ea))) {
    return { ok: false, reason: "outOfRange" };
  }
  return {
    ok: true,
    unknown,
    ea,
    t: v.t,
    dt: v.dt,
    factor,
    answer: unknown === "factor" ? factor : ea / eaFactor(unit.ea),
    answerUnit: unknown === "factor" ? "" : unit.ea,
  };
}

/* ------------------------------------------------------ Arrhenius plot */

export interface FitPoint {
  /** Kelvin */
  t: number;
  k: number;
  /** 1/T and ln k, the plotted pair. */
  x: number;
  y: number;
}

export interface ArrheniusFit {
  ok: true;
  points: FitPoint[];
  slope: number;
  intercept: number;
  /** J/mol */
  ea: number;
  a: number;
  r2: number;
}

export type FitOutcome = ArrheniusFit | ArrheniusFailure;

const SPLIT = /[\s,;]+/;
/** Without the comma: a decimal-comma line such as "555 3,52e-7" or "555;3,52e-7". */
const SPLIT_NO_COMMA = /[\s;]+/;

function splitPointLine(line: string): string[] {
  const parts = line.split(SPLIT).filter((part) => part !== "");
  if (parts.length === 2) {
    return parts;
  }
  const retry = line.split(SPLIT_NO_COMMA).filter((part) => part !== "");
  return retry.length === 2 ? retry : parts;
}
const NEWLINE = /\r?\n/;

/** Parses "T k" lines (comma, space, tab or semicolon separated; a decimal comma is read
 * when the line otherwise has exactly two fields). */
export function parsePoints(
  text: string,
  tempUnit: string
): { points: FitPoint[] } | ArrheniusFailure {
  const points: FitPoint[] = [];
  const lines = text.split(NEWLINE);
  for (const [index, raw] of lines.entries()) {
    const line = raw.trim();
    if (line === "") {
      continue;
    }
    const parts = splitPointLine(line);
    const temperature =
      parts.length === 2 ? parseMeasuredNumber(parts[0]) : null;
    const rate = parts.length === 2 ? parseMeasuredNumber(parts[1]) : null;
    if (temperature === null || rate === null || !(rate > 0)) {
      return { ok: false, reason: "badLine", line: index + 1 };
    }
    const kelvin = toKelvin(temperature, tempUnit);
    if (!(kelvin > 0)) {
      return { ok: false, reason: "badLine", line: index + 1 };
    }
    points.push({ t: kelvin, k: rate, x: 1 / kelvin, y: Math.log(rate) });
  }
  return { points };
}

export function fitArrhenius(text: string, tempUnit: string): FitOutcome {
  const parsed = parsePoints(text, tempUnit);
  if ("ok" in parsed) {
    return parsed;
  }
  const { points } = parsed;
  if (points.length < 2) {
    return { ok: false, reason: "tooFewPoints" };
  }
  const n = points.length;
  const meanX = points.reduce((sum, p) => sum + p.x, 0) / n;
  const meanY = points.reduce((sum, p) => sum + p.y, 0) / n;
  const sxx = points.reduce((sum, p) => sum + (p.x - meanX) ** 2, 0);
  if (sxx === 0) {
    return { ok: false, reason: "allSameTemperature" };
  }
  const sxy = points.reduce((sum, p) => sum + (p.x - meanX) * (p.y - meanY), 0);
  const syy = points.reduce((sum, p) => sum + (p.y - meanY) ** 2, 0);
  const slope = sxy / sxx;
  const intercept = meanY - slope * meanX;
  return {
    ok: true,
    points,
    slope,
    intercept,
    ea: -slope * R,
    a: Math.exp(intercept),
    r2: syy === 0 ? 1 : (sxy * sxy) / (sxx * syy),
  };
}
